import { DOCUMENT } from '@angular/common';
import { EnvironmentProviders, ENVIRONMENT_INITIALIZER, inject, makeEnvironmentProviders } from '@angular/core';
import { Router } from '@angular/router';
import { LoggingService } from '../logging/logging.service';

/**
 * Click interceptor that intercepts clicks on SHUI web components which would
 * otherwise perform a hard `window.location.href = href` navigation, and
 * routes them through the Angular `Router` instead.
 *
 * Several SHUI components (`<sh-breadcrumb-item>`, `<sh-icon>`, `<sh-card>`,
 * etc.) listen for clicks on themselves and, when their `href` property is
 * set, do a full-page `window.location.href` assignment. Angular's
 * `RouterLink` directive sets that `href` host property (via `@HostBinding`)
 * for any element it is applied to, so `[routerLink]` on these custom
 * elements unintentionally triggers a hard navigation that wipes the
 * in-memory NGXS state and re-shows the startup splash spinner.
 *
 * Catching the click in the document's capture phase (before it reaches the
 * SHUI shadow-DOM handler) lets us replace it with an Angular router
 * navigation and avoid the reload entirely.
 *
 * Modifier-key clicks (Ctrl/Cmd/Shift/Alt or non-primary mouse buttons) are
 * passed through so users can still open targets in a new tab. Anchors
 * (`#...`), absolute URLs, and `mailto:` / `tel:` links are also left
 * untouched so the browser handles them normally.
 */

const HARD_NAV_TAG_NAMES = new Set(['sh-breadcrumb-item', 'sh-icon', 'sh-card']);

let registeredRouter: Router | null = null;
let registeredLogger: LoggingService | null = null;

const handleClick = (event: Event): void => {
    if (!(event instanceof MouseEvent)) {
        return;
    }
    if (
        event.defaultPrevented ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
    ) {
        return;
    }

    // Walk the composed path so we also catch clicks that originate inside
    // the element's shadow DOM.
    const target = event
        .composedPath()
        .find(
            (node): node is HTMLElement =>
                node instanceof HTMLElement && HARD_NAV_TAG_NAMES.has(node.tagName.toLowerCase())
        );
    if (!target) {
        return;
    }
    if (target.hasAttribute('active') || target.hasAttribute('disabled')) {
        registeredLogger?.debug('[sh-breadcrumb-routing] click ignored: target active/disabled', {
            tagName: target.tagName.toLowerCase()
        });
        return;
    }

    const href = target.getAttribute('href') ?? (target as HTMLElement & { href?: string }).href;
    if (typeof href !== 'string' || !href.startsWith('/')) {
        registeredLogger?.debug('[sh-breadcrumb-routing] click ignored: non-internal href', {
            tagName: target.tagName.toLowerCase(),
            href
        });
        return;
    }

    // Prevent the SHUI shadow-DOM click handler from running and performing
    // a hard `window.location.href` navigation.
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    if (registeredRouter) {
        registeredLogger?.debug('[sh-breadcrumb-routing] intercepted click, routing via Angular', {
            tagName: target.tagName.toLowerCase(),
            href
        });
        void registeredRouter.navigateByUrl(href);
    } else if (typeof window !== 'undefined') {
        // Fallback: if the Angular Router has not been wired up yet, use the
        // History API directly so we still get an SPA-style URL change
        // without a full reload.
        registeredLogger?.debug(
            '[sh-breadcrumb-routing] intercepted click, router unavailable, using history.pushState',
            { tagName: target.tagName.toLowerCase(), href }
        );
        window.history.pushState({}, '', href);
        window.dispatchEvent(new PopStateEvent('popstate'));
    }
};

let listenerRegistered = false;
const ensureListener = (doc: Document): void => {
    if (listenerRegistered) {
        return;
    }
    listenerRegistered = true;
    doc.addEventListener('click', handleClick, { capture: true });
};

// Eagerly register at module load time so the listener is active before any
// Angular environment initializer runs. Guarded for SSR / test environments.
if (typeof document !== 'undefined') {
    ensureListener(document);
}

export const provideShBreadcrumbRouting = (): EnvironmentProviders =>
    makeEnvironmentProviders([
        {
            provide: ENVIRONMENT_INITIALIZER,
            multi: true,
            useValue: () => {
                registeredRouter = inject(Router);
                registeredLogger = inject(LoggingService);
                ensureListener(inject(DOCUMENT));
                registeredLogger.debug('[sh-breadcrumb-routing] provider initialized');
            }
        }
    ]);
